import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../ui/Button/Button';
import Dialog from '../../../ui/Dialog/Dialog';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ProgressBar from '../../../ui/Progress/ProgressBar';
import { finesService, errorMessage } from '../../../services/finesService';
import { followJob } from '../../../services/followJob';
import { Pill } from './ui';
import { paidOn } from './rtaPayments';

// "Read RTA account payments now" (owner, 15 Oct 2026): the weekly read of the company RTA account's receipts, run by
// hand - the engine signs in with its RTA account (and the e-mail code), opens the transaction history and saves every
// receipt it does not hold yet. It never pays or submits anything on RTA. Started after the menu's confirmation; the
// read carries on in the engine when this dialog is closed.

const STATE = { running: 'running', done: 'done' };   // anything else (failed / stopped / waiting) ends the bar as stopped

// the engine's status may name the running read by its id or as the job itself
const jobIdOf = (j) => (typeof j === 'string' ? j : j?.jobId || j?.id || '');

export default function RtaReadNowModal({ onClose, onDone }) {
    const [jobId, setJobId] = useState('');
    const [job, setJob] = useState(null);
    const [waiting, setWaiting] = useState('');       // the RTA sign-in is paused: nothing started
    const [joined, setJoined] = useState(false);      // a read was already running: this dialog follows it
    const [status, setStatus] = useState(null);       // last read / next run
    const [error, setError] = useState(null);
    const [stopping, setStopping] = useState(false);
    const started = useRef(false);
    const alive = useRef(true);
    const changed = useRef(false);

    const loadStatus = () => finesService.rtaPaymentsStatus().then((st) => { if (alive.current) setStatus(st); return st; }).catch(() => null);

    const follow = async (id) => {
        setJobId(id);
        try {
            // a closed dialog stops following (the read itself goes on in the engine)
            const last = await followJob(() => (alive.current ? finesService.rtaReadNowJob(id) : Promise.resolve({ status: 'closed' })), {
                onUpdate: (j) => { if (alive.current && j?.status !== 'closed') { setError(null); setJob(j); } },
                onRetry: (e) => { if (alive.current) setError(`Lost contact with the engine (${errorMessage(e)}) - still following the read, retrying…`); },
            });
            if (last?.status === 'done' || last?.receipts_new) changed.current = true;
        } catch (e) { if (alive.current) setError(errorMessage(e)); }
        loadStatus();
    };

    useEffect(() => {
        alive.current = true;
        if (!started.current) {
            started.current = true;
            (async () => {
                loadStatus();
                try {
                    const r = await finesService.rtaReadNow();
                    if (r?.waiting) { setWaiting(r.waiting); return; }
                    setJob({ status: 'running', done: 0, total: 0, stage: 'starting' });
                    await follow(r.jobId);
                } catch (e) {
                    // a read already runs (the weekly one, or another user's): follow it rather than start a second
                    if (e?.response?.status === 409) {
                        const id = jobIdOf(e.response.data?.jobId) || jobIdOf((await loadStatus())?.running_job);
                        if (id) { setJoined(true); setJob({ status: 'running', done: 0, total: 0, stage: 'following the read already running' }); await follow(id); return; }
                    }
                    if (alive.current) setError(errorMessage(e));
                }
            })();
        }
        return () => { alive.current = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const stop = async () => {
        if (!jobId) return;
        setStopping(true);
        try { await finesService.rtaReadNowStop(jobId); } catch (e) { setError(errorMessage(e)); }
        setStopping(false);
    };
    const close = () => { if (changed.current) onDone?.(); onClose(); };

    const running = job?.status === 'running';
    const state = STATE[job?.status] || 'stopped';
    const note = [
        job?.stage,
        job?.status === 'waiting' ? 'waiting: the RTA sign-in is paused' : '',
        job?.note,
        job?.error,
    ].filter(Boolean).join(' · ');

    return (
        <Dialog open onClose={close} size="lg" title="Read RTA account payments">
            <div className="text-sm text-gray-600 mb-3">
                The engine signs in to the company RTA account, reads its transaction history and saves every receipt it does not hold yet -
                the same read it runs by itself every week. Nothing is paid or changed on RTA.
                {status && (
                    <div className="mt-1 text-gray-700">
                        Last read: <b>{paidOn(status.last_read_at) || 'never'}</b>
                        {status.last_window_to ? <> (up to {paidOn(status.last_window_to)})</> : null}
                        {status.next_run ? <> · next by itself: <b>{paidOn(status.next_run)}</b></> : null}
                        {status.receipts !== undefined ? <> · {status.receipts} receipt(s), {status.lines ?? 0} line(s) kept</> : null}
                    </div>
                )}
            </div>
            {error && <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {waiting && (
                <div role="status" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Not started: the RTA sign-in is paused ({waiting}). Nothing was read - try again once it is free.
                </div>
            )}
            {joined && <div className="mb-2 text-sm text-gray-700">A read was already running - this window follows it.</div>}
            {!job && !waiting && !error && <LoadingSpinner message="Starting the read…" />}
            {!job && (waiting || error) && <div className="flex justify-end"><Button size="sm" onClick={close}>Close</Button></div>}
            {job && (
                <div className="flex flex-col gap-3">
                    <ProgressBar done={job.done} total={job.total} unit="receipts" state={state}
                                 label="Reading the company RTA account" note={note} />
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                        <div className="flex flex-wrap gap-2">
                            {!running && <Pill tone={job.receipts_new ? 'green' : 'gray'}>New receipts: {job.receipts_new ?? 0}</Pill>}
                            {!running && <Pill tone="slate">Fine lines: {job.fines ?? 0}</Pill>}
                        </div>
                        <div className="flex gap-2">
                            {running && <Button size="sm" variant="danger" leadingIcon={<Icon glyph={ICONS.close} size="sm" />} onClick={stop} disabled={!jobId || stopping} isLoading={stopping}>Stop</Button>}
                            {!running && <Button size="sm" onClick={close}>Close</Button>}
                        </div>
                    </div>
                </div>
            )}
        </Dialog>
    );
}
